import React from "react"
import { Redirect, useLocation } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import type { MessagesHostContract } from "@internal/messages"
import EmployeeOnlySpaceContainer from "#/components/employee-only-containers/EmployeeOnlySpaceContainer"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { EMAIL_VERIFICATION_CACHE_KEY } from "#/components/EmailVerification/useEmailVerification"
import { PageHeader } from "#/features/global-ui/page-header"
import { PageLayout, PageLoading } from "#/features/global-ui/page-layout"
import { updateCarrotPlanMessageDismissTimestamp } from "#/components/views/modal/utils/localStorageUtils"
import { reportError, reportErrorMessage } from "#/utils/ErrorReporting"
import { api, providerApi } from "./messagesHostApi"

const MessagesFeature = React.lazy(() => import("@internal/messages").then((m) => ({ default: m.MessagesFeature })))

const components: MessagesHostContract["components"] = {
  PageLayout,
  PageHeader,
  EmployeeOnlySpace: EmployeeOnlySpaceContainer
}

// Everything gathered here is module-scoped and never changes identity: `api` and `providerApi` are
// module consts (see ./messagesHostApi), the reporters and the localStorage helper are module
// functions, and `components` is built once above. Holding them here rather than inline in the
// `useMemo` below is what keeps that memo's dependency list short and provably complete — the
// alternative, listing them as dependencies, is rejected by `react-hooks/exhaustive-deps` because
// mutating an outer-scope value cannot re-render the component.
//
// `providerApi` IS ALWAYS SUPPLIED — there is no flag. Provider messaging shows itself: a member with
// Healthie conversations sees them, a member with none gets an empty list from the BFF and the page
// looks exactly as it did before the source existed. Which member each request is for comes from the
// session by way of the patient-access identity lookup, so there is nothing here to switch on. The
// contract keeps the key OPTIONAL for hosts and tests that have no provider transport, but this host
// is not one of them.
//
// One contract field is NOT here, and it is the memo's only dependency: `employeeId`, which arrives
// with the host's user info.
const staticHost = {
  api,
  providerApi,
  onCarrotPlanMessageSeen: updateCarrotPlanMessageDismissTimestamp,
  reportError,
  reportErrorMessage,
  components
}

export function ConsumerMessagesPage(): JSX.Element {
  const { personalEmail, employeeId } = useCurrentUser()
  const { state } = useLocation<{ previousPath: string }>()

  // SUBSCRIBED to the verification query rather than read once out of the cache. The original uses
  // `queryClient.getQueryData`, which is not reactive: a verification that resolves after this
  // mounts never re-renders the page, so the gate keeps whatever answer it had at mount.
  //
  // `enabled: false` and no fetch of its own — this page has never issued the request and should
  // not start. Whoever owns the query (`useEmailVerification`) fetches it; this only wants to be
  // told when the answer lands. Deliberately not the hook itself, which has `refetchOnMount: true`.
  const { data: isEmailVerified } = useQuery<boolean>([EMAIL_VERIFICATION_CACHE_KEY], { enabled: false })

  const host = React.useMemo<MessagesHostContract>(() => ({ ...staticHost, employeeId }), [employeeId])

  // A `<Redirect>`, NOT a `history.replace` in the render body as the original does. That call
  // returns, so the same render pass carried on and mounted the feature — a member who is not
  // allowed to be here got the transports fired from its effects before the route changed under
  // them. Returning here is what makes the gate a gate.
  //
  // The condition is also just the condition: the original's second `return !isEmailVerified` can
  // only ever be `true`, since the line above it has already returned for every truthy value.
  if (personalEmail !== null && !isEmailVerified) {
    return <Redirect to={{ pathname: state?.previousPath || "/", state: { showVerifyEmailModal: true } }} />
  }

  return (
    // `PageLoading` rather than `LoadingInner`: this route renders into the app shell, and
    // `PageLoading` puts its spinner inside the same `PageLayout` the loaded feature uses. It is
    // also what `MessagesRoute` shows while the program flags resolve, so the two loading states
    // that run back to back here look like one.
    <React.Suspense fallback={<PageLoading />}>
      <MessagesFeature host={host} />
    </React.Suspense>
  )
}
